'use client';
import { useEffect, useState } from 'react';
import { fetchWithCache } from '@/lib/use-resource';

// Holdings table needs 6 different artifacts: portfolio composition,
// universe scores, snapshot history, per-symbol P&L, alpha-zone-day counts,
// and self-percentile bands. Originally 6 separate useEffect+fetch blocks
// in HoldingsList.tsx — extracted here so the component file shrinks and
// the data layer is testable in isolation.
//
// All fetches go through the shared useResource cache, so other components
// hitting these endpoints don't re-fetch.

export interface PortfolioFile {
  symbols: string[];
  weights?: Record<string, number>;
  cashWeight?: number;
  /** Per-symbol fractional shares (or normalized share-of-NAV units). When
   *  present, the UI live-computes weights as shares × current_price /
   *  sum(shares × current_price) so weights drift with market prices instead
   *  of staying frozen at the last edit-weights save. */
  shares?: Record<string, number>;
  /** Per-position cost basis (price paid). Preserved across rebalances. */
  entryPrices?: Record<string, number>;
  /** Per-position open date — preserved across rebalances. */
  entryDates?: Record<string, string>;
  /** Book NAV in $ (default 1_000_000). Converts normalised shares → real
   *  share counts in the UI. Set in portfolio.json to match your actual book. */
  bookNav?: number;
  updatedAt?: string;
}

export interface ScoreRow {
  q: number | null;
  v: number | null;
  sector: string | null;
  name: string | null;
  price: number | null;
  marketCap: number | null;
}

export interface HoldingHistory {
  w1: number | null;
  m1: number | null;
  m3: number | null;
  m6: number | null;
  y1: number | null;
}

export interface HoldingPnl {
  dayPct: number | null;
  mtdPct: number | null;
  ytdPct: number | null;
  off52wHigh: number | null;
}

export interface HoldingSelfPctile {
  q: number | null;
  v: number | null;
  c: number | null;
  qRegime: string;
  vRegime: string;
  cRegime: string;
}

interface SnapshotPerHolding {
  symbol: string;
  historicalComposites?: HoldingHistory;
}

interface AxisPercentile {
  current: number;
  percentile: number | null;
  mean: number;
  stdev: number;
  count: number;
  regime: string;
  firstObs: string | null;
  lastObs: string | null;
}

interface SelfPercentilesFile {
  bySymbol?: Record<string, { q?: AxisPercentile; v?: AxisPercentile; composite?: AxisPercentile }>;
}

interface PnlFile {
  bySymbol?: Record<string, HoldingPnl>;
}

export interface HoldingsData {
  portfolio: PortfolioFile | null;
  scores: Map<string, ScoreRow> | null;
  historyBySymbol: Map<string, HoldingHistory>;
  pnlBySymbol: Map<string, HoldingPnl>;
  alphaDaysBySymbol: Map<string, number>;
  selfPctileBySymbol: Map<string, HoldingSelfPctile>;
}

export function useHoldingsData(): HoldingsData {
  const [portfolio, setPortfolio] = useState<PortfolioFile | null>(null);
  const [scores, setScores] = useState<Map<string, ScoreRow> | null>(null);
  const [historyBySymbol, setHistoryBySymbol] = useState<Map<string, HoldingHistory>>(new Map());
  const [pnlBySymbol, setPnlBySymbol] = useState<Map<string, HoldingPnl>>(new Map());
  const [alphaDaysBySymbol, setAlphaDaysBySymbol] = useState<Map<string, number>>(new Map());
  const [selfPctileBySymbol, setSelfPctileBySymbol] = useState<Map<string, HoldingSelfPctile>>(new Map());

  useEffect(() => {
    fetchWithCache<PortfolioFile>('/api/portfolio').then(setPortfolio).catch(() => {});

    fetchWithCache<unknown>('/api/stocks').then((d: unknown) => {
      const arr: Array<{ symbol: string; name?: string | null; qualityScore?: number | null; valuationScore?: number | null; sector?: string | null; price?: number | null; marketCap?: number | null }> =
        Array.isArray(d)
          ? d
          : ((d as { stocks?: unknown[]; data?: unknown[] })?.stocks as never[] || (d as { data?: unknown[] })?.data as never[] || []);
      const m = new Map<string, ScoreRow>();
      for (const r of arr) {
        m.set(r.symbol, { q: r.qualityScore ?? null, v: r.valuationScore ?? null, sector: r.sector ?? null, name: r.name ?? null, price: r.price ?? null, marketCap: r.marketCap ?? null });
      }
      setScores(m);
    }).catch(() => {});

    fetchWithCache<{ rebalance?: { perHolding?: SnapshotPerHolding[] } }>('/api/portfolio-snapshot')
      .then((d) => {
        const m = new Map<string, HoldingHistory>();
        for (const h of d?.rebalance?.perHolding ?? []) {
          if (h.historicalComposites) m.set(h.symbol, h.historicalComposites);
        }
        setHistoryBySymbol(m);
      })
      .catch(() => { /* keep empty map */ });

    fetchWithCache<PnlFile>('/api/holdings-pnl')
      .then((d) => {
        const m = new Map<string, HoldingPnl>();
        for (const [sym, row] of Object.entries(d?.bySymbol ?? {})) {
          m.set(sym, { dayPct: row.dayPct, mtdPct: row.mtdPct, ytdPct: row.ytdPct, off52wHigh: row.off52wHigh });
        }
        setPnlBySymbol(m);
      })
      .catch(() => { /* keep empty map */ });

    fetchWithCache<{ alphaZone?: { current?: Array<{ symbol: string; enteredOn: string }> } }>('/api/zone-changes')
      .then((d) => {
        // For each stock currently in the Alpha Zone, count calendar days
        // since its first entry within the lookback window.
        const today = new Date().toISOString().slice(0, 10);
        const m = new Map<string, number>();
        for (const row of d?.alphaZone?.current ?? []) {
          const days = Math.max(0, Math.round(
            (Date.parse(today) - Date.parse(row.enteredOn)) / 86_400_000,
          ));
          m.set(row.symbol, days);
        }
        setAlphaDaysBySymbol(m);
      })
      .catch(() => { /* keep empty map */ });

    fetchWithCache<SelfPercentilesFile | null>('/api/self-percentiles')
      .then((d) => {
        const m = new Map<string, HoldingSelfPctile>();
        for (const [sym, row] of Object.entries(d?.bySymbol ?? {})) {
          m.set(sym, {
            q: row?.q?.percentile ?? null,
            v: row?.v?.percentile ?? null,
            c: row?.composite?.percentile ?? null,
            qRegime: row?.q?.regime ?? '',
            vRegime: row?.v?.regime ?? '',
            cRegime: row?.composite?.regime ?? '',
          });
        }
        setSelfPctileBySymbol(m);
      })
      .catch(() => { /* keep empty map */ });
  }, []);

  return { portfolio, scores, historyBySymbol, pnlBySymbol, alphaDaysBySymbol, selfPctileBySymbol };
}
